import { tourLength } from "@jpjj/solver-core/routing";
import { state } from "./state";
import { LEVELS } from "./levels";
import { openLength, leagues } from "./geometry";
import { OPT_EPS, starsFor } from "./scoring";
import { $ } from "../dom";
import { buildBoard, clearPreview, renderRoute, routeHasCrossing } from "./board";
import { cancelAutoScroll, scrollToChart } from "./scroll";
import { openReveal, prepareSolves, resetReveal } from "../reveal/reveal";

/** Length of the route as charted right now: the open path while ports remain,
 *  the full closed round (homeward leg included) once every port is visited. */
export function currentRouteLength(): number {
  const order = [0, ...state.route];
  const d = state.dist();
  return state.route.length === state.portCount()
    ? tourLength(order, d)
    : openLength(order, d);
}

/* ---------- Level load ---------- */
export function loadLevel(i: number): void {
  state.idx = i;
  state.route = [...state.saved[i]];
  clearPreview();
  buildBoard();
  recompute();
  updateNav();
}

/* ---------- Recompute (render + HUD + stars + persistence) ---------- */
export function recompute(): void {
  const L = state.level();
  const complete = state.route.length === state.portCount();
  const len = currentRouteLength();

  renderRoute();

  // persist the working route; fold a completed round into the best record
  state.saved[state.idx] = [...state.route];
  const rec = state.records[state.idx];
  if (complete && (rec.order.length === 0 || len < rec.length)) {
    state.records[state.idx] = { order: [0, ...state.route], length: len };
  }
  const best = state.records[state.idx];

  // HUD
  $("levelVal").textContent = (state.idx + 1) + " / " + LEVELS.length;
  $("distVal").textContent = state.route.length === 0 ? "0.0" : leagues(len);
  $("portVal").textContent = state.route.length + " / " + state.portCount();
  $("levelName").innerHTML = "Voyage " + (state.idx + 1) + " — <b>" + L.name + "</b>";
  $("bestLine").textContent = best.order.length
    ? `Best round this voyage: ${leagues(best.length)} leagues`
    : "No completed round yet";

  // stars rate the best completed round of this voyage
  const stars = starsFor(L, best.order.length ? best.length : 0);
  const starEls = $("stars").children;
  for (let i = 0; i < 3; i++) starEls[i].classList.toggle("on", i < stars);

  // message
  const msg = $("msg");
  msg.className = "msg";
  if (!complete) {
    if (state.route.length === 0) {
      msg.textContent = "Tap a port to chart your first leg — the ship sails in the order you tap.";
    } else {
      msg.textContent = "Charting… tap the next port, or tap a numbered port to drop it from the route.";
    }
  } else if (len <= L.opt + OPT_EPS) {
    msg.classList.add("optimal");
    msg.textContent = "✦ The proven shortest round — masterful navigation!";
  } else if (routeHasCrossing()) {
    msg.classList.add("warn");
    msg.textContent = "Your course crosses itself — uncrossing two legs always shortens a round.";
  } else if (len <= L.opt * 1.05) {
    msg.textContent = "A tight round — but a shorter one exists. Try re-ordering a port or two.";
  } else {
    msg.textContent = "The spice is delivered, but the ship sailed far further than it had to.";
  }

  if (stars === 3 && !state.completed[state.idx]) {
    state.completed[state.idx] = true;
    updateNav();
  }
  $("nextBtn").classList.toggle("glow", stars === 3);
}

/* ---------- Nav ---------- */
export function buildDots(): void {
  const d = $("dots");
  d.innerHTML = "";
  LEVELS.forEach((_, i) => {
    const b = document.createElement("div");
    b.className = "dot";
    b.textContent = String(i + 1);
    b.addEventListener("click", () => loadLevel(i));
    d.appendChild(b);
  });
}

export function updateNav(): void {
  const dots = $("dots").children;
  for (let i = 0; i < dots.length; i++) {
    dots[i].classList.toggle("current", i === state.idx);
    dots[i].classList.toggle("done", state.completed[i]);
    dots[i].textContent = state.completed[i] ? "✓" : String(i + 1);
  }
  ($("prevBtn") as HTMLButtonElement).disabled = state.idx === 0;
  $("nextBtn").textContent = state.idx === LEVELS.length - 1 ? "Face the optimizer ✦" : "Next ▸";
}

export function goPrev(): void {
  if (state.idx > 0) loadLevel(state.idx - 1);
}

export function goNext(): void {
  if (state.idx < LEVELS.length - 1) {
    loadLevel(state.idx + 1);
    scrollToChart(); // mirror of the completion glide: back up to the field
    return;
  }
  // the reveal compares completed rounds, so every voyage needs one
  const missing = state.records.findIndex((r) => r.order.length === 0);
  if (missing === -1) {
    cancelAutoScroll();
    openReveal();
  } else {
    loadLevel(missing);
    scrollToChart();
    const msg = $("msg");
    msg.className = "msg warn";
    msg.textContent = `Voyage ${missing + 1} has no completed round yet — finish every voyage to face the optimizer.`;
  }
}

export function resetLevel(): void {
  cancelAutoScroll();
  state.route = [];
  clearPreview();
  recompute();
}

/* ---------- Run lifecycle ---------- */
export function startGame(): void {
  state.phase = "play";
  loadLevel(0);
  // kick off the per-voyage Held–Karp solves (in their Web Worker) during
  // play so the reveal is instant. Failures are swallowed here — the reveal
  // re-awaits each solve and shows a graceful message instead.
  prepareSolves().catch(() => { /* solver warm-up failed; handled at reveal time */ });
}

export function restartGame(): void {
  state.completed = LEVELS.map(() => false);
  state.saved = LEVELS.map(() => []);
  state.records = LEVELS.map(() => ({ order: [], length: 0 }));
  $("solverOverlay").classList.add("hidden");
  $("learnOverlay").classList.add("hidden");
  resetReveal();
  loadLevel(0);
  scrollToChart(); // the page may still be parked at the old verdict
}
